import { createElement, type ReactNode } from "react";

import type { ActivityAdapter } from "@/app/integration/contracts";
import {
  letterSoundLevels,
  pictureWordMatchLevels,
  rhythmPhraseLevels,
  type EnglishActivityType,
  type LetterSoundPayload,
  type PictureWordMatchPayload,
  type RhythmPhrasePayload
} from "@/content/english";
import type { LevelDefinition } from "@/domain";
import type {
  ActivityHostActionCreators,
  ActivityHostInstruction,
  ActivityHostRenderContext
} from "@/features/activity-host";
import type { CurriculumAudioPlayer } from "@/features/audio";

import {
  englishChoicePlayerActions,
  englishLetterSoundAdapter,
  englishPictureWordMatchAdapter,
  type EnglishChoicePlayerAction,
  type EnglishChoicePlayerRenderModel,
  type EnglishChoicePlayerState
} from "./choice-adapters";
import {
  EnglishChoiceActivity,
  EnglishRhythmActivity
} from "./EnglishPlayerRenderers";
import type { EnglishActivityHostBridge } from "./audio-bridge";
import {
  englishRhythmPhrasesAdapter,
  englishRhythmPlayerActions,
  type EnglishRhythmPlayerAction,
  type EnglishRhythmPlayerRenderModel,
  type EnglishRhythmPlayerState
} from "./rhythm-adapter";

export interface EnglishPlayerBindingBase<
  TKind extends EnglishActivityType,
  TPayload,
  TState,
  TAction,
  TRenderModel
> {
  readonly kind: TKind;
  readonly levelId: string;
  readonly level: LevelDefinition<TPayload>;
  readonly instruction: ActivityHostInstruction;
  readonly adapter: ActivityAdapter<
    TPayload,
    TState,
    TAction,
    TRenderModel
  >;
  readonly actions: ActivityHostActionCreators<TAction>;
  readonly renderActivity: (
    props: EnglishPlayerRenderActivityProps<TAction, TRenderModel>
  ) => ReactNode;
}

export interface EnglishPlayerRenderActivityProps<
  TAction,
  TRenderModel
> {
  readonly audioPlayer?: CurriculumAudioPlayer;
  readonly bridge: EnglishActivityHostBridge<TAction>;
  readonly context: ActivityHostRenderContext<TAction, TRenderModel>;
}

export type EnglishLetterSoundPlayerBinding = EnglishPlayerBindingBase<
  "letter-sound",
  LetterSoundPayload,
  EnglishChoicePlayerState,
  EnglishChoicePlayerAction,
  EnglishChoicePlayerRenderModel
>;

export type EnglishPictureWordPlayerBinding = EnglishPlayerBindingBase<
  "picture-word-match",
  PictureWordMatchPayload,
  EnglishChoicePlayerState,
  EnglishChoicePlayerAction,
  EnglishChoicePlayerRenderModel
>;

export type EnglishRhythmPlayerBinding = EnglishPlayerBindingBase<
  "rhythm-phrases",
  RhythmPhrasePayload,
  EnglishRhythmPlayerState,
  EnglishRhythmPlayerAction,
  EnglishRhythmPlayerRenderModel
>;

export type EnglishPlayerBinding =
  | EnglishLetterSoundPlayerBinding
  | EnglishPictureWordPlayerBinding
  | EnglishRhythmPlayerBinding;

function createInstruction(
  level: LevelDefinition
): ActivityHostInstruction {
  return {
    caption: level.instruction,
    title: level.title
  };
}

function renderEnglishChoiceActivity(
  props: EnglishPlayerRenderActivityProps<
    EnglishChoicePlayerAction,
    EnglishChoicePlayerRenderModel
  >
): ReactNode {
  return createElement(EnglishChoiceActivity, props);
}

function renderEnglishRhythmActivity(
  props: EnglishPlayerRenderActivityProps<
    EnglishRhythmPlayerAction,
    EnglishRhythmPlayerRenderModel
  >
): ReactNode {
  return createElement(EnglishRhythmActivity, props);
}

export const englishLetterSoundPlayerBindings =
  letterSoundLevels.map<EnglishLetterSoundPlayerBinding>((level) => ({
    kind: "letter-sound",
    levelId: level.id,
    level,
    instruction: createInstruction(level),
    adapter: englishLetterSoundAdapter,
    actions: englishChoicePlayerActions,
    renderActivity: renderEnglishChoiceActivity
  }));

export const englishPictureWordPlayerBindings =
  pictureWordMatchLevels.map<EnglishPictureWordPlayerBinding>((level) => ({
    kind: "picture-word-match",
    levelId: level.id,
    level,
    instruction: createInstruction(level),
    adapter: englishPictureWordMatchAdapter,
    actions: englishChoicePlayerActions,
    renderActivity: renderEnglishChoiceActivity
  }));

export const englishRhythmPlayerBindings =
  rhythmPhraseLevels.map<EnglishRhythmPlayerBinding>((level) => ({
    kind: "rhythm-phrases",
    levelId: level.id,
    level,
    instruction: createInstruction(level),
    adapter: englishRhythmPhrasesAdapter,
    actions: englishRhythmPlayerActions,
    renderActivity: renderEnglishRhythmActivity
  }));

export const englishPlayerBindings: readonly EnglishPlayerBinding[] = [
  ...englishLetterSoundPlayerBindings,
  ...englishPictureWordPlayerBindings,
  ...englishRhythmPlayerBindings
];

export function getEnglishPlayerBinding(
  levelId: string
): EnglishPlayerBinding | undefined {
  return englishPlayerBindings.find((binding) => binding.levelId === levelId);
}
